import { Dispatch, FormEventHandler, MutableRefObject, SetStateAction, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Company,
  Destination,
  Language,
  ProductionDivision,
  ProductionStatus,
  ScheduleListSearchConditions,
  TreeCompany,
} from '../../../api/model';
import DatePicker from '../../atoms/DatePicker';
import MultiSelect from '../../atoms/MultiSelect';
import OrganizationTreeMultiSelect, { getOptions } from '../../modules/OrganizationTreeMultiSelect';
import SlideToggle from '../../modules/SlideToggle';
import MultiSelectStringArray from './MultiSelectStringArray';

interface Props {
  openSearchConditions: boolean;
  defaultConditions: ScheduleListSearchConditions;
  modelNames: string[];
  destinations: Destination[];
  productionDevisions: ProductionDivision[];
  treeCompanies: TreeCompany[];
  companies: Company[];
  statuses: ProductionStatus[];
  languages: Language[];
  conditionsRef: MutableRefObject<ScheduleListSearchConditions>;
  setShowChildList: Dispatch<SetStateAction<string[]>>;
  setConditionsAndSearch: (value: ScheduleListSearchConditions) => void;
}

/**
 * 日程一覧検索条件表示
 * @param param0
 * @returns
 */
function ScheduleListSerchCondition({
  openSearchConditions,
  defaultConditions,
  conditionsRef,
  modelNames,
  destinations,
  productionDevisions,
  statuses,
  languages,
  treeCompanies,
  companies,
  setShowChildList,
  setConditionsAndSearch,
}: Props) {
  // フォームのデフォルト値
  const defaultValues = useMemo(() => {
    const modelUserIds = defaultConditions.model_user_ids?.map((x) => `user_${x}`);
    return { ...defaultConditions, model_user_ids: modelUserIds };
  }, [defaultConditions]);

  // フォーム情報
  const { control, register, handleSubmit } = useForm({ defaultValues });

  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const mysList: string[] = [];
  for (let x = 2020; x <= 2050; x += 1) {
    mysList.push(x.toString());
  }

  // フォーム送信
  const onSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    void handleSubmit((data) => {
      // 組織コンポネントを使っているため、user_{number} の形でデータが送られるので number に変換する
      const modelUserIds =
        data.model_user_ids != null
          ? data.model_user_ids.flatMap((x) => {
              const splited = x.split('_');
              return splited.length === 2 ? parseInt(splited[1], 10) : [];
            })
          : undefined;
      const next: ScheduleListSearchConditions = {
        ...conditionsRef.current,
        production_no: data.production_no,
        model_names: data.model_names,
        mys: data.mys,
        destination_ids: data.destination_ids,
        production_division_ids: data.production_division_ids,
        model_user_ids: modelUserIds,
        completion_from: data.completion_from,
        completion_end: data.completion_end,
        production_status_ids: data.production_status_ids,
        language_ids: data.language_ids,
        maker_company_ids: data.maker_company_ids,
      };

      // 検索実行など
      setConditionsAndSearch(next);
    })();
  };

  return (
    <SlideToggle open={openSearchConditions}>
      <form id="production-search-conditions" onSubmit={onSubmit}>
        <div className="wfr scondition trigfr" style={{ display: 'block' }}>
          <div className="relativefr">
            <div className="sepfr3 mbs">
              <div className="labip1">
                <div>
                  <label htmlFor="production_no">{t('資料管理No')}</label>
                  <input type="text" id="production_no" {...register('production_no')} />
                </div>
                <div>
                  <label htmlFor="model_names">{t('機種名')}</label>
                  <MultiSelectStringArray
                    id="model_names"
                    name="model_names"
                    options={modelNames}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x}
                    getLabel={(x) => x}
                  />
                </div>
                <div>
                  <label htmlFor="mys">{t('M/Y')}</label>
                  <MultiSelectStringArray
                    id="mys"
                    name="mys"
                    options={mysList}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x}
                    getLabel={(x) => x}
                  />
                </div>
                <div>
                  <label htmlFor="destination_ids">{t('仕向け')}</label>
                  <MultiSelect
                    id="destination_ids"
                    name="destination_ids"
                    options={destinations}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x.destination_id}
                    getLabel={(x) => x.destination_name}
                  />
                </div>
              </div>
              <div className="labip1">
                <div>
                  <label htmlFor="production_division_ids">{t('資料区分')}</label>
                  <MultiSelect
                    id="production_division_ids"
                    name="production_division_ids"
                    options={productionDevisions}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x.production_division_id}
                    getLabel={(x) => x.production_division_name}
                  />
                </div>
                <div>
                  <label htmlFor="model_user_ids">{t('機種担当')}</label>
                  <span className="dropdown">
                    <OrganizationTreeMultiSelect
                      id="model_user_ids"
                      name="model_user_ids"
                      control={control}
                      options={getOptions(treeCompanies, language === 'ja')}
                      selectableTypes={['user']}
                      isDisabled={false}
                    />
                  </span>
                </div>
                <div>
                  <label htmlFor="completion_from">{t('完成日From')}</label>
                  <DatePicker
                    id="completion_from"
                    name="completion_from"
                    isDisabled={false}
                    control={control}
                    displayMark
                  />
                </div>
                <div>
                  <label htmlFor="completion_end">{t('完成日End')}</label>
                  <DatePicker
                    id="completion_end"
                    name="completion_end"
                    isDisabled={false}
                    control={control}
                    displayMark
                  />
                </div>
              </div>

              <div className="labip1">
                <div>
                  <label htmlFor="production_status_ids">{t('ステータス')}</label>
                  <MultiSelect
                    id="production_status_ids"
                    name="production_status_ids"
                    options={statuses}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x.production_status_id}
                    getLabel={(x) => x.production_status_name}
                  />
                </div>
                <div>
                  <label htmlFor="language_ids">{t('言語')}</label>
                  <MultiSelect
                    id="language_ids"
                    name="language_ids"
                    options={languages}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x.language_id}
                    getLabel={(x) => x.language_name}
                  />
                </div>
                <div>
                  <label htmlFor="maker_company_ids">{t('原作メーカー')}</label>
                  <MultiSelect
                    id="maker_company_ids"
                    name="maker_company_ids"
                    options={companies}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x.company_id}
                    getLabel={(x) => x.company_name}
                  />
                </div>
              </div>
            </div>
            <div className="controlfr w20 schedule-list-search">
              <span>
                <input
                  id="search"
                  name="search"
                  type="submit"
                  value={t('検索').toString()}
                  className="searchbtn"
                  onClick={() => {
                    setShowChildList([]);
                  }}
                />
              </span>
            </div>
          </div>
        </div>
      </form>
    </SlideToggle>
  );
}

export default ScheduleListSerchCondition;
